import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] },
});

const TEAM = [
  {
    name: "Marcus Loos",
    role: "Schreinermeister · 4. Generation",
    desc: "Marcus Loos führt die Schreinerei mit dem gleichen Anspruch an Qualität und Handwerkskunst, der die Familie Loos seit über 80 Jahren auszeichnet. In seiner Werkstatt in Lorenkamp 4 entstehen Fenster, Türen und Möbel nach Maß.",
    area: "Schreinerei",
    color: "#8B5E3C",
  },
  {
    name: "Sabine Loos",
    role: "Bestatterin · 4. Generation",
    desc: "Sabine Loos begleitet Trauernde seit Jahren mit Einfühlungsvermögen und Menschlichkeit. Im Bestattungshaus an der Mühlenstraße 6 sorgt sie und ihr Team dafür, dass Abschiede würdevoll und persönlich gestaltet werden.",
    area: "Bestattungen",
    color: "#3E4C59",
  },
];

const UeberUns = () => (
  <main>
    {/* HERO */}
    <section className="relative h-[60vh] overflow-hidden" data-testid="ueber-uns-hero">
      {/* PLATZHALTER: durch echtes Familien-/Team-Foto ersetzen */}
      <div
        className="absolute inset-0 bg-cover bg-center animate-ken-burns"
        style={{ backgroundImage: "url('https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?q=80&w=1920&auto=format&fit=crop')" }}
      />
      <div className="absolute inset-0 bg-[#2B2B2B]/55" />
      <div className="absolute inset-0 flex items-end">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 w-full pb-16">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.2 }}>
            <p className="text-[#C9A66B] uppercase tracking-[0.35em] text-xs font-manrope mb-3">Über uns</p>
            <h1 className="font-playfair text-4xl sm:text-5xl lg:text-6xl text-white leading-tight">
              Vier Generationen.<br />Eine Familie.
            </h1>
          </motion.div>
        </div>
      </div>
    </section>

    {/* STORY */}
    <section className="py-24 bg-[#F8F5F0]" data-testid="story-section">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <motion.div {...fadeUp(0)}>
            <p className="uppercase tracking-[0.3em] text-xs text-[#8B5E3C] font-manrope mb-3">Unsere Geschichte</p>
            <h2 className="font-playfair text-3xl sm:text-4xl text-[#2B2B2B] mb-6">Aus einer Werkstatt, zwei Berufungen</h2>
            <p className="text-[#2B2B2B]/65 font-manrope leading-relaxed text-sm sm:text-base mb-4">
              Was als bescheidene Schreinerei in Haltern am See begann, ist heute ein Familienunternehmen in der vierten Generation mit über 80 Jahren Geschichte. Die Familie Loos steht für ehrliches Handwerk, Verlässlichkeit und menschliche Nähe.
            </p>
            <p className="text-[#2B2B2B]/65 font-manrope leading-relaxed text-sm sm:text-base">
              Die enge Verbindung zwischen Schreinerei und Bestattungen ist kein Zufall: Beide Bereiche entstammen demselben Ethos – der Überzeugung, dass handwerkliche Sorgfalt und persönliche Fürsorge untrennbar zusammengehören.
            </p>
          </motion.div>
          <motion.div {...fadeUp(0.15)} className="relative h-72 overflow-hidden">
            {/* PLATZHALTER: durch echtes Werkstatt-Innenaufnahme ersetzen */}
            <div
              className="absolute inset-0 bg-cover bg-center"
              style={{ backgroundImage: "url('https://images.unsplash.com/photo-1504148455328-c376907d081c?q=80&w=900&auto=format&fit=crop')" }}
            />
            <div className="absolute bottom-3 left-3 bg-black/50 text-white/60 text-[10px] font-manrope px-2 py-1">
              PLATZHALTER – Werkstatt Lorenkamp 4
            </div>
          </motion.div>
        </div>
      </div>
    </section>

    {/* TIMELINE */}
    <section className="py-16 bg-[#2B2B2B]" data-testid="generation-timeline">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.p {...fadeUp(0)} className="text-center uppercase tracking-[0.3em] text-xs text-[#C9A66B] font-manrope mb-3">
          Generationen
        </motion.p>
        <motion.h2 {...fadeUp(0.1)} className="text-center font-playfair text-3xl text-white mb-14">
          80 Jahre Familientradition
        </motion.h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {[
            { gen: "1.", year: "ca. 1940", text: "Gründung der Schreinerei in Haltern am See." },
            { gen: "2.", year: "ca. 1965", text: "Erweiterung auf das Bestattungshandwerk." },
            { gen: "3.", year: "ca. 1990", text: "Modernisierung, Ausbau des Bestattungshauses." },
            { gen: "4.", year: "Heute", text: "Marcus & Sabine Loos führen das Familienunternehmen." },
          ].map((g, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: i * 0.12 }}
              className="border border-white/10 p-6"
              data-testid={`gen-card-${i}`}
            >
              <div className="font-playfair text-4xl text-[#C9A66B] mb-1">{g.gen}</div>
              <div className="text-xs font-manrope text-white/40 uppercase tracking-widest mb-3">{g.year}</div>
              <p className="text-white/65 text-sm font-manrope leading-relaxed">{g.text}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>

    {/* TEAM */}
    <section className="py-24 bg-[#F8F5F0]" data-testid="team-section">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.p {...fadeUp(0)} className="text-center uppercase tracking-[0.3em] text-xs text-[#8B5E3C] font-manrope mb-3">
          Unser Team
        </motion.p>
        <motion.h2 {...fadeUp(0.1)} className="text-center font-playfair text-3xl sm:text-4xl text-[#2B2B2B] mb-14">
          Die Menschen hinter Loos
        </motion.h2>
        <div className="grid md:grid-cols-2 gap-8">
          {TEAM.map((member, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 25 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: i * 0.15 }}
              className="bg-white border border-[#8B5E3C]/10 p-8"
              data-testid={`team-card-${i}`}
            >
              {/* PLATZHALTER: durch echtes Porträtfoto ersetzen */}
              <div className="w-20 h-20 rounded-full mb-5 overflow-hidden border-2" style={{ borderColor: member.color }}>
                <div className="w-full h-full bg-cover bg-center" style={{
                  backgroundImage: "url('https://images.unsplash.com/photo-1560250097-0b93528c311a?q=80&w=200&auto=format&fit=crop')"
                }} />
              </div>
              <div className="text-[10px] uppercase tracking-[0.3em] font-manrope mb-1" style={{ color: member.color }}>
                {member.area}
              </div>
              <h3 className="font-playfair text-2xl text-[#2B2B2B] mb-1">{member.name}</h3>
              <p className="text-xs text-[#2B2B2B]/45 font-manrope mb-4">{member.role}</p>
              <p className="text-[#2B2B2B]/60 text-sm font-manrope leading-relaxed">{member.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>

    {/* CTA */}
    <section className="py-20 bg-[#8B5E3C]">
      <div className="max-w-3xl mx-auto px-4 text-center">
        <motion.h2 {...fadeUp(0)} className="font-playfair text-3xl sm:text-4xl text-white mb-4">
          Wie können wir Ihnen helfen?
        </motion.h2>
        <motion.p {...fadeUp(0.1)} className="text-white/70 font-manrope mb-8">
          Sprechen Sie uns an – ob für Ihr Zuhause oder in schwerer Zeit.
        </motion.p>
        <motion.div {...fadeUp(0.2)} className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link to="/schreinerei" className="inline-flex items-center justify-center gap-2 bg-white text-[#8B5E3C] px-6 py-3 text-sm font-manrope font-semibold hover:bg-[#F8F5F0] transition-colors">
            Zur Schreinerei <ArrowRight size={14} />
          </Link>
          <Link to="/bestattungen" className="inline-flex items-center justify-center gap-2 border border-white text-white px-6 py-3 text-sm font-manrope hover:bg-white hover:text-[#8B5E3C] transition-all">
            Zu den Bestattungen <ArrowRight size={14} />
          </Link>
        </motion.div>
      </div>
    </section>
  </main>
);

export default UeberUns;
